import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { LngLatLike } from 'mapbox-gl';

import { useGlMap } from '../../contexts/context';

// https://docs.mapbox.com/mapbox-gl-js/guides/install/#excluding-mapbox-gl-js-explicitly-from-transpilation
// eslint-disable-next-line
import mapboxgl from '!mapbox-gl';
import { checkZoomEditable } from '../../utils/helpers/utils.ts';
import { useSelector } from 'react-redux';
import { selectZoom } from '../../store/index.ts';

const MARKER_ANCHOR_OPTIONS = {
  center: 'center',
  top: 'top',
  bottom: 'bottom',
  left: 'left',
  right: 'right',
  topLeft: 'top-left',
  topRight: 'top-right',
  bottomLeft: 'bottom-left',
  bottomRight: 'bottom-right',
};

const useMarker = ({
  id,
  initialLocation,
  initialAnchor,
  draggable = false,
}: {
  id?;
  initialLocation: mapboxgl.LngLatLike;
  initialAnchor?: mapboxgl.Anchor;
  draggable?: boolean;
}) => {
  const elementRef = useRef<HTMLDivElement | null>(null);
  if (!elementRef.current) {
    elementRef.current = document.createElement('div');
  }

  if (id) {
    elementRef.current.id = id;
  }

  /** @type {import('react').RefObject<mapboxgl.Marker>} */
  const markerRef = useRef<mapboxgl.Marker | null>(null);
  if (!markerRef.current) {
    markerRef.current = new mapboxgl.Marker({
      element: elementRef.current,
      draggable,
      // anchor is not editable via mapboxgl.Marker API. The only way to change it is to
      // create new marker with the same state and different anchor value.
      anchor: initialAnchor,
    });
    markerRef.current.setLngLat(initialLocation);
  }

  const glMap = useGlMap();
  const zoom = useSelector(selectZoom);

  useEffect(() => {
    markerRef.current?.setDraggable(checkZoomEditable(zoom));
  }, [zoom]);

  useEffect(() => {
    if (!glMap || !markerRef.current) {
      return;
    }

    markerRef.current.addTo(glMap);
    return () => {
      if (!markerRef.current) {
        return;
      }

      markerRef.current.remove();
    };
  }, [glMap]);

  return {
    marker: markerRef.current,
    element: elementRef.current,
  };
};

export interface MarkerProps {
  id?;
  location;
  children;
  initialAnchor?;
}

const Marker = ({ id, location, children, initialAnchor }: MarkerProps) => {
  const { marker, element } = useMarker({
    id,
    initialLocation: location,
    initialAnchor: initialAnchor || MARKER_ANCHOR_OPTIONS.bottom,
  });

  useEffect(() => {
    marker.setLngLat(location);
  }, [location, marker]);

  return createPortal(children, element);
};

interface BeforeCoordinatesUpdateFunc {
  (coords: number[]): number[] | null;
}

export interface DraggableMarkerProps extends MarkerProps {
  onDragEnd;
  onDragStart?;
  beforeCoordinatesUpdate: BeforeCoordinatesUpdateFunc;
}

const DraggableMarker = ({
  location,
  onDragEnd,
  onDragStart,
  children,
  beforeCoordinatesUpdate,
  id,
  initialAnchor,
}: DraggableMarkerProps) => {
  const { marker, element } = useMarker({
    id,
    initialLocation: location,
    draggable: true,
    initialAnchor: initialAnchor || MARKER_ANCHOR_OPTIONS.bottom,
  });

  useEffect(() => {
    marker.setLngLat(location);
  }, [location, marker]);

  const map = useGlMap();

  const locationRef = useRef();
  locationRef.current = location;
  // should be not a reactive value as long as it should not recreate subscriptions on events when user already moves marker
  const beforeCoordinatesUpdateRef = useRef<BeforeCoordinatesUpdateFunc>();
  beforeCoordinatesUpdateRef.current = beforeCoordinatesUpdate;
  useEffect(() => {
    if (!map) {
      return;
    }

    const correctMarkerCoords = (callback?) => {
      const markerCoords = marker.getLngLat().toArray();

      const correctedMarkerCoords = beforeCoordinatesUpdateRef.current
        ? beforeCoordinatesUpdateRef.current(markerCoords)
        : markerCoords;

      // if marker location could not be set, use latest location provided to the marker from the outside
      marker.setLngLat(
        (correctedMarkerCoords as LngLatLike) || locationRef.current,
      );

      if (callback) {
        callback();
      }
    };

    const handleDragStart = () =>
      correctMarkerCoords(() => {
        if (onDragStart) {
          onDragStart(marker.getLngLat().toArray());
        }
      });
    marker.on('dragstart', handleDragStart);

    const handleDrag = () => {
      if (!checkZoomEditable(map.getZoom())) {
        return;
      }
      correctMarkerCoords();
    };
    marker.on('drag', handleDrag);

    const handleDragend = () => {
      correctMarkerCoords(() => onDragEnd(marker.getLngLat().toArray()));
    };

    marker.on('dragend', handleDragend);

    return () => {
      marker.off('dragstart', handleDragStart);
      marker.off('drag', handleDrag);
      marker.off('dragend', handleDragend);
    };
  }, [marker, onDragStart, map, onDragEnd]);

  return createPortal(children, element);
};

export interface MapCenterDraggableMarkerProps extends MarkerProps {
  onDragEnd;
  beforeCoordinatesUpdate: BeforeCoordinatesUpdateFunc;
}

const MapCenterDraggableMarker = ({
  id,
  location,
  children,
  onDragEnd,
  beforeCoordinatesUpdate,
  initialAnchor,
}: MapCenterDraggableMarkerProps) => {
  const { marker, element } = useMarker({
    id,
    initialLocation: location,
    initialAnchor: initialAnchor || MARKER_ANCHOR_OPTIONS.bottom,
  });

  const map = useGlMap();

  useEffect(() => {
    marker.setLngLat(location);
  }, [location, marker]);

  const locationRef = useRef();
  locationRef.current = location;
  // should be not a reactive value as long as it should not recreate subscriptions on events when user already moves marker
  const beforeCoordinatesUpdateRef = useRef<BeforeCoordinatesUpdateFunc>();
  beforeCoordinatesUpdateRef.current = beforeCoordinatesUpdate;
  useEffect(() => {
    if (!map) {
      return;
    }

    const updateMarkerCoords = (callback?) => {
      const mapCenter = map.getCenter().toArray();

      const updatedMarkerCoords = beforeCoordinatesUpdateRef.current
        ? beforeCoordinatesUpdateRef.current(mapCenter)
        : mapCenter;

      // if marker location could not be set, use latest location provided to the marker from the outside
      marker.setLngLat(
        (updatedMarkerCoords as LngLatLike) || locationRef.current,
      );

      if (callback) {
        callback();
      }
    };

    const handleMapMove = () => updateMarkerCoords();
    const handleMapMoveEnd = () => {
      map.off('move', handleMapMove);
      updateMarkerCoords(() => onDragEnd(marker.getLngLat().toArray()));
    };

    const handleMapInteractionStart = () => {
      if (!checkZoomEditable(map.getZoom())) {
        return;
      }

      map.on('move', handleMapMove);
      map.once('moveend', handleMapMoveEnd);
    };

    map.on('dragstart', handleMapInteractionStart);
    map.on('zoomstart', handleMapInteractionStart);

    return () => {
      map.off('dragstart', handleMapInteractionStart);
      map.off('zoomstart', handleMapInteractionStart);
    };
  }, [map, marker, onDragEnd]);

  return createPortal(children, element);
};

export default Marker;
export {
  Marker,
  DraggableMarker,
  MapCenterDraggableMarker,
  MARKER_ANCHOR_OPTIONS,
};
